/**
 * Column definitions for the event table.
 *
 * The same shape as `features/leads/table.ts`, deliberately — the two tables now
 * render through the same component, and keeping the module layout identical
 * means a change to one is obvious to apply to the other.
 *
 * The Event column is not in here: it is always shown, always first, and is the
 * only one that renders two lines, so it has no width entry and cannot be
 * switched off.
 */
import type { Cell as SharedCell, ColumnFilters as SharedColumnFilters, ColumnMeta } from '@/shared/components/DataTable'
import { formatDate, todayIso } from '@/shared/util/dates'
import { fmtKrSum } from '@/shared/util/money'
import {
  EVENT_STAGES,
  EVENT_STAGE_LABEL,
  EVENT_STAGE_STYLES,
  EVENT_TIER_STYLES,
} from '@/types/events'
import type { ScoredEvent } from './repository/eventsRepository'

/**
 * The columns an event is worked by — decision 64.
 *
 * The build used to carry sixteen: stage, the A·B·C axes, outreach by, region,
 * category, who goes, venue, cost tier, date confidence, next action, source.
 * Those came from `importEvents.ts`, which harvested a list of candidates, and
 * they describe **how an event entered the book** rather than how it is run.
 *
 * They are not deleted. Every one is still on the record — the drawer shows
 * them, and the provenance ones sit together under "How it entered the book".
 * What changed is that a planning screen stopped being a harvest report.
 *
 * Two of these are the old columns under the design's names: `type` is the
 * category, and `status` is the stage. Same field, same values — renaming
 * rather than replacing, the way decision 68 treated Fit and Score.
 */
export type ColumnKey =
  | 'type'
  | 'when'
  | 'location'
  | 'status'
  | 'score'
  | 'owner'
  | 'budget'
  | 'attendance'

export const COLUMN_META: Record<ColumnKey, ColumnMeta> = {
  type: { label: 'Type', width: 1.1 },
  when: { label: 'Dates', width: 1 },
  /** Venue and country in one cell: neither is useful without the other. */
  location: { label: 'Location', width: 1.3 },
  status: { label: 'Status', width: 0.9 },
  /** The computed 0–100 with its tier, or a dash where scoring was skipped. */
  score: { label: 'Score', width: 0.85 },
  owner: { label: 'Owner / ambassador', width: 1.2 },
  budget: { label: 'Budget', width: 0.9 },
  /** Registered and attended, as one pair — the second is read against the first. */
  attendance: { label: 'Reg. / att.', width: 0.9 },
}

export const COLUMN_KEYS = Object.keys(COLUMN_META) as ColumnKey[]

/** All eight: the design draws one table, not a default and a picker. */
export const DEFAULT_COLUMNS: ColumnKey[] = [...COLUMN_KEYS]

export const FILTERABLE_COLUMNS: ColumnKey[] = ['status', 'type']

export type SortKey = ColumnKey | 'name'

/**
 * Columns whose header carries a multi-select filter.
 *
 * The four with a small, closed set of values. Owner is deliberately absent —
 * the search box answers ownership better with `mine` and `unassigned`, and a
 * dropdown on every column turns the header into a control panel.
 */

export type ColumnFilters = SharedColumnFilters<ColumnKey>
export type Cell = SharedCell<ColumnKey>

/** The value a filter compares against, for one event and one column. */
export function filterValue(e: ScoredEvent, key: ColumnKey): string {
  switch (key) {
    case 'status':
      return e.stage
    case 'type':
      return e.category
    default:
      return ''
  }
}

export function passesFilters(e: ScoredEvent, filters: ColumnFilters): boolean {
  return FILTERABLE_COLUMNS.every((key) => {
    const selected = filters[key]
    if (!selected || selected.length === 0) return true
    return selected.includes(filterValue(e, key))
  })
}

const today = todayIso

/**
 * An event is historical once its last day has passed.
 *
 * Undated events are never historical: "we do not know when it runs" is not the
 * same as "it is over", and hiding them would quietly bury the ones that most
 * need a date found.
 */
export function isPast(e: { startDate: string; endDate: string }): boolean {
  const last = e.endDate || e.startDate
  return Boolean(last) && last < today()
}

function joinPeople(e: ScoredEvent): string {
  return e.assignments.map((a) => a.personName).join(', ')
}

export function buildCells(e: ScoredEvent, columns: ColumnKey[]): Cell[] {
  return columns.map((key): Cell => {
    switch (key) {
      case 'type':
        return { key, badge: false, text: e.category || '—' }
      case 'when':
        return {
          key,
          badge: false,
          // A date somebody is unsure of is still a date, and the confidence
          // travels with it rather than being a column of its own.
          text: e.startDate ? formatDate(e.startDate) : e.dateConfidence || 'no date',
          title:
            e.startDate && e.endDate ? `${e.startDate} → ${e.endDate}` : e.dateConfidence,
        }
      case 'location':
        return {
          key,
          badge: false,
          text: [e.venue, e.country].filter(Boolean).join(
) || '—',
          title: [e.venue, e.region, e.country].filter(Boolean).join(
),
        }
      case 'status': {
        const st = EVENT_STAGE_STYLES[e.stage]
        return { key, badge: true, text: EVENT_STAGE_LABEL[e.stage], bg: st.bg, color: st.color }
      }
      case 'score': {
        // A dash, not a zero. An event nobody has scored must not sort below one
        // that was scored and found weak — and the tooltip says why it is blank.
        if (e.computedScore === null || e.computedTier === null) {
          return { key, badge: false, text: '—', title: e.skipped ?? 'Not scored' }
        }
        const st = EVENT_TIER_STYLES[e.computedTier]
        return {
          key,
          badge: true,
          text: `${e.computedScore} ${e.computedTier}`,
          bg: st.bg,
          color: st.color,
        }
      }
      case 'owner': {
        // The owner is who is accountable; the ambassadors are who is going.
        // One cell, because on a planning screen they answer one question.
        const going = joinPeople(e)
        return {
          key,
          badge: false,
          text: [e.ownerName, going].filter(Boolean).join(' · ') || 'Unassigned',
          title: going ? `Owner ${e.ownerName || 'none'} · going: ${going}` : undefined,
        }
      }
      case 'budget':
        return {
          key,
          badge: false,
          // Budget against what was actually spent, where both exist.
          text: e.budgetNok === null ? '—' : fmtKrSum(e.budgetNok),
          title:
            e.actualSpendNok === null
              ? undefined
              : `Spent ${fmtKrSum(e.actualSpendNok)}`,
        }
      case 'attendance':
        return {
          key,
          badge: false,
          text:
            e.registered === null && e.attendees === null
              ? '—'
              : `${e.registered ?? '–'} / ${e.attendees ?? '–'}`,
          title: 'Registered / attended',
        }
    }
  })
}

export function sortValue(e: ScoredEvent, sortBy: SortKey): string | number | null {
  switch (sortBy) {
    case 'type':
      return e.category.toLowerCase() || null
    case 'when':
      return e.startDate || null
    case 'location':
      return [e.venue, e.country].filter(Boolean).join(
).toLowerCase() || null
    case 'status':
      return EVENT_STAGES.findIndex((s) => s.key === e.stage)
    case 'score':
      return e.computedScore
    case 'owner':
      return (e.ownerName || e.ownerId).toLowerCase() || null
    case 'budget':
      return e.budgetNok
    case 'attendance':
      // Attended first: it is the figure the row is read for once the event
      // has happened, and registered is what it is judged against.
      return e.attendees ?? e.registered
    default:
      return e.name.toLowerCase()
  }
}

export function sortEvents(rows: ScoredEvent[], sortBy: SortKey, dir: 'asc' | 'desc'): ScoredEvent[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const av = sortValue(a, sortBy)
    const bv = sortValue(b, sortBy)
    if (av === null && bv === null) return a.name.localeCompare(b.name)
    if (av === null) return 1
    if (bv === null) return -1
    if (av < bv) return -sign
    if (av > bv) return sign
    // Name is the tiebreaker, so equal scores do not shuffle on re-render.
    return a.name.localeCompare(b.name)
  })
}
